import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchLeadFollowUps,
  isWonRow,
  markFollowUpDone,
  rowName,
  undoFollowUpDone,
  UNDO_MS,
  type LeadFollowUpRow,
} from './leadFollowUpsApi';

/**
 * Personal mode of FollowUpsAdminPage, with no team assignment controls.
 * 2026-10-07 (Manak option 1): reads the same lead follow-ups feed as the
 * AURA Sales app, tags won leads "Won", and closes a follow-up with one
 * "Done" (Undo for 5 s).
 */
export function PersonalFollowUps() {
  const [rows, setRows] = useState<LeadFollowUpRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ row: LeadFollowUpRow; previous: string | null } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(() => {
    setError(null);
    fetchLeadFollowUps()
      .then(setRows)
      .catch(() => setError('Could not load follow-ups. Retry to check again.'));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const showToast = (t: { row: LeadFollowUpRow; previous: string | null } | null) => {
    if (timer.current) clearTimeout(timer.current);
    setToast(t);
    if (t) timer.current = setTimeout(() => setToast(null), UNDO_MS);
  };

  const done = async (row: LeadFollowUpRow) => {
    setRows((rs) => (rs ?? []).filter((r) => r.id !== row.id));
    try {
      const previous = await markFollowUpDone(row.id);
      showToast({ row, previous: previous ?? row.scheduledAt });
    } catch {
      setRows((rs) => [...(rs ?? []), row]);
      setError('Could not mark the follow-up done.');
    }
  };

  const undo = async () => {
    if (!toast) return;
    const { row, previous } = toast;
    showToast(null);
    try {
      if (previous) await undoFollowUpDone(row.id, previous);
      load();
    } catch {
      setError('Could not undo.');
    }
  };

  const now = Date.now();
  const pending = (rows ?? []).filter((r) => r.status === 'PENDING');
  const overdue = pending.filter((r) => r.scheduledAt && new Date(r.scheduledAt).getTime() < now).length;
  const sorted = [...pending].sort((a, b) => {
    if (!a.scheduledAt) return 1;
    if (!b.scheduledAt) return -1;
    return a.scheduledAt.localeCompare(b.scheduledAt);
  });

  return <div className="space-y-4">
    <header className="flex justify-between">
      <div>
        <h1 className="text-2xl font-semibold">Follow-ups</h1>
        <p>My follow-ups.</p>
      </div>
      <button className="btn" data-tour="followups-refresh" onClick={load}>Refresh</button>
    </header>
    {error && <p className="text-sm text-red-600">{error} <button className="underline" onClick={load}>Retry</button></p>}
    {rows === null ? (!error && <p>Loading follow-ups…</p>) : <>
      <p data-tour="followups-count">{pending.length} scheduled · {overdue} overdue</p>
      {sorted.length === 0 ? <p data-tour="followups-list">No follow-ups due.</p> : <ul data-tour="followups-list" className="space-y-3">{sorted.map(row => {
        const late = !!row.scheduledAt && new Date(row.scheduledAt).getTime() < now;
        return <li key={row.id} className="card p-4 flex justify-between items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <strong>{rowName(row)}</strong>
              {isWonRow(row) && <span data-testid={`fu-won-tag-${row.id}`} className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">WON</span>}
            </div>
            <p className={late ? 'text-red-600' : undefined}>
              {row.scheduledAt
                ? new Date(row.scheduledAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                : 'No date set'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {row.scheduledAt && <button type="button" data-testid={`fu-done-${row.id}`} onClick={() => void done(row)}
              className="rounded-lg border border-emerald-200 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50">✓ Mark done</button>}
            <Link className="btn" to="/leads">Open my leads</Link>
          </div>
        </li>;
      })}</ul>}
    </>}
    {toast && <div role="status" className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-4 rounded-lg bg-gray-900 px-4 py-3 text-sm text-white shadow-lg">
      <span>Follow-up marked done</span>
      <button type="button" onClick={() => void undo()} className="font-semibold text-emerald-300 hover:text-emerald-200">Undo</button>
    </div>}
  </div>;
}
